/*
 * <product-card-variants>: lets a shopper pick a variant from dropdowns on the product card
 * itself, so a multi-variant product can be added to cart without opening the quick-add modal.
 *
 * Why this isn't Dawn's variant picker: every id in snippets/product-variant-picker.liquid is
 * built from section.id, so N cards in one section would produce duplicate ids, and
 * assets/product-info.js resolves a variant by fetching the product section per change — one
 * request per dropdown change per card. Matching on the client is the only workable option for a
 * grid of cards, and the variant list is not otherwise exposed to the page.
 *
 * Everything else still goes through Dawn: the add itself is the theme's <product-form> (which
 * publishes cartUpdate only on success), and the form stays a real Shopify product form with a
 * hidden id input and a name="add" submit, so cart apps that bind to it (GoKwik, Shopflo) read
 * the correct variant at click time.
 *
 * Money strings are formatted in Liquid and read from the JSON payload — never built here.
 */
if (!customElements.get('product-card-variants')) {
  customElements.define(
    'product-card-variants',
    class ProductCardVariants extends HTMLElement {
      connectedCallback() {
        const payload = this.querySelector('[data-card-variants]');
        if (!payload) return;

        try {
          this.variants = JSON.parse(payload.textContent);
        } catch (error) {
          return;
        }
        if (!Array.isArray(this.variants) || this.variants.length === 0) return;

        this.card = this.closest('.product-card');
        this.selects = Array.from(this.querySelectorAll('[data-option-index]'));
        if (this.selects.length === 0) return;

        this.idInput = this.querySelector('.product-variant-id');
        this.submit = this.querySelector('[type="submit"]');
        this.addLabel = this.querySelector('[data-add-label]');
        this.soldOutLabel = this.querySelector('[data-sold-out-label]');
        this.saveTemplate = this.dataset.saveLabel || '';

        this.priceCurrent = this.card?.querySelector('.product-card__price-current');
        this.priceCompare = this.card?.querySelector('.product-card__price-compare');
        this.savePill = this.card?.querySelector('.product-card__save');

        this.selections = this.selects.map((select) => select.value);

        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);

        this.refresh();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      onChange(event) {
        const select = event.target.closest('[data-option-index]');
        if (!select) return;
        this.selections[Number(select.dataset.optionIndex)] = select.value;
        this.refresh();
      }

      /*
       * Rebuilds each dropdown so it only offers values that exist AND are in stock for the
       * options chosen before it, then resolves the matching variant. Selections cascade: index 1
       * is filtered by the (possibly just corrected) value at index 0, and so on.
       */
      refresh() {
        const anyAvailable = this.variants.some((variant) => variant.a);

        this.selects.forEach((select, index) => {
          const values = this.valuesFor(index, anyAvailable);
          if (values.length === 0) return;
          if (!values.includes(this.selections[index])) this.selections[index] = values[0];
          this.renderSelect(select, values, this.selections[index]);
        });

        this.applyVariant(this.findVariant());
      }

      // When every variant is sold out there is nothing available to offer, so fall back to the
      // full value list and let the button fall to "Sold out" rather than emptying the dropdowns.
      valuesFor(index, availableOnly) {
        const prefix = this.selections.slice(0, index);
        const values = [];

        this.variants.forEach((variant) => {
          if (availableOnly && !variant.a) return;
          if (!prefix.every((value, position) => variant.o[position] === value)) return;
          if (!values.includes(variant.o[index])) values.push(variant.o[index]);
        });

        return values;
      }

      renderSelect(select, values, selected) {
        const current = Array.from(select.options).map((option) => option.value);
        const unchanged = current.length === values.length && current.every((value, i) => value === values[i]);

        if (!unchanged) select.replaceChildren(...values.map((value) => new Option(value, value)));
        if (select.value !== selected) select.value = selected;
      }

      findVariant() {
        return (
          this.variants.find((variant) => this.selections.every((value, index) => variant.o[index] === value)) || null
        );
      }

      applyVariant(variant) {
        const available = Boolean(variant && variant.a);

        // Same contract as Dawn's updateVariantInputs: set the value, then fire change so anything
        // listening to the form (cart apps, pixels) sees the current variant.
        if (this.idInput) {
          this.idInput.value = variant ? variant.id : '';
          this.idInput.disabled = !available;
          this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.submit) this.submit.disabled = !available;
        if (this.addLabel) this.addLabel.hidden = !available;
        if (this.soldOutLabel) this.soldOutLabel.hidden = available;

        if (variant) this.updatePrice(variant);

        this.card?.dispatchEvent(
          new CustomEvent('product-card:variant-change', {
            bubbles: true,
            detail: { variantId: variant ? variant.id : null },
          })
        );
      }

      updatePrice(variant) {
        if (this.priceCurrent) this.priceCurrent.textContent = variant.p;

        if (this.priceCompare) {
          this.priceCompare.textContent = variant.c || '';
          this.priceCompare.hidden = !variant.c;
        }

        if (this.savePill) {
          this.savePill.textContent = variant.s ? this.saveTemplate.replace('__amount__', variant.s) : '';
          this.savePill.hidden = !variant.s;
        }
      }
    }
  );
}
